import { useCallback, useMemo, useState } from 'react';
import {
  applyEdgeChanges,
  applyNodeChanges,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
} from '@xyflow/react';

import { NodeType, StaticNodeType } from '../../hooks/use-sidebar-control.types.js';
import { INITIAL_ENROLL_NODE_ID, LAYOUT } from '../constants';
import {
  healEdgesAfterNodeRemoval,
  insertNodeAtPlaceholder,
  recalculateLinearPosition,
  shouldResetToStart,
} from '../workflow-graph-actions';
import { mapCanvasContent } from '../../mappers/map-canvas-content.js';

export const useWorkflowGraph = () => {
  const [isNodeSidebarOpen, setIsNodeSidebarOpen] = useState(false);

  const openSidebar = useCallback(() => setIsNodeSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setIsNodeSidebarOpen(false), []);

  // When add node is clicked, we store nodeId in order to place new node correctly.
  const [activePlusNodeId, setActivePlusNodeId] = useState<string | null>(null);

  const openSidebarForPlusNode = useCallback((plusNodeId: string) => {
    setActivePlusNodeId(plusNodeId);
    setIsNodeSidebarOpen(true);
  }, []);

  const [nodes, setNodes] = useState<Node[]>(() => [
    {
      id: INITIAL_ENROLL_NODE_ID,
      type: NodeType.Enroll,
      position: { x: 200, y: 200 },
      data: {},
    },
  ]);
  const [edges, setEdges] = useState<Edge[]>([]);

  const addNodeAtPlaceholder = useCallback(
    (nodeType: NodeType, data?: Record<string, unknown>) => {
      if (!activePlusNodeId) {
        return;
      }

      // replace the placeholder with the new node + placeholder
      setNodes((prevNodes) => {
        const result = insertNodeAtPlaceholder({
          nodes: prevNodes,
          edges,
          placeholderId: activePlusNodeId,
          nodeType,
          data,
        });
        setEdges(result.edges);
        return result.nodes;
      });

      setIsNodeSidebarOpen(false);
      setActivePlusNodeId(null);
    },
    [activePlusNodeId, edges]
  );

  const addEnroll = useCallback(
    (data: Record<string, unknown>) => {
      if (activePlusNodeId) {
        addNodeAtPlaceholder(NodeType.Enroll, data);
      }
    },
    [activePlusNodeId, addNodeAtPlaceholder]
  );

  const onNodesChange = useCallback((changedNodes: NodeChange[]) => {
    // Remove changes for add nodes so applyNodeChanges never removes them.
    const filteredChanges = changedNodes.filter((c) => {
      if (c.type !== 'remove') {
        return true;
      }
      return !c.id.startsWith('add-');
    });

    const removedNodes = filteredChanges.filter((node) => node.type === 'remove');
    const removedNodeIds = removedNodes.map((r) => r.id);
    const filteredRemovedNodeIds = removedNodeIds.filter((id) => !id.startsWith('add-'));

    // ensure that nodes are still connected after removal
    if (filteredRemovedNodeIds.length > 0) {
      setNodes((prevNodes) => {
        const nextNodes = applyNodeChanges(filteredChanges, prevNodes);

        // fallback to display initial Enroll node if everything is deleted
        if (filteredRemovedNodeIds.length > 0 && shouldResetToStart(nextNodes)) {
          setEdges([]);
          return [
            {
              id: INITIAL_ENROLL_NODE_ID,
              type: NodeType.Enroll,
              position: { x: 200, y: 200 },
              data: {},
            },
          ];
        }

        return recalculateLinearPosition(nextNodes);
      });

      setEdges((prevEdges) =>
        healEdgesAfterNodeRemoval({
          edges: prevEdges,
          removedNodeIds: filteredRemovedNodeIds,
        })
      );
    } else {
      setNodes((prevNodes) => applyNodeChanges(filteredChanges, prevNodes));
    }
  }, []);

  const onEdgesChange = useCallback((edgeChanges: EdgeChange[]) => {
    const filteredChanges = edgeChanges.filter((change) => change.type !== 'remove');
    setEdges((prevEdges) => applyEdgeChanges(filteredChanges, prevEdges));
  }, []);

  // Add nodes get onClick so clicking it opens sidebar
  const nodesWithHandlers = useMemo(
    () =>
      nodes.map((node) => {
        if (node.type === StaticNodeType.Add) {
          return { ...node, data: { ...node.data, onClick: openSidebarForPlusNode } };
        }

        return node;
      }),
    [nodes, openSidebarForPlusNode]
  );

  const isFirstEnrollConfigured = useCallback((node: Node) => {
    const data = node.data as {
      orBlocks?: { andBlocks?: { entityType?: string; entityId?: string }[] }[];
    };
    if (!Array.isArray(data?.orBlocks)) {
      return false;
    }

    return data.orBlocks.some((orBlock) => {
      if (!Array.isArray(orBlock?.andBlocks)) {
        return false;
      }
      return orBlock.andBlocks.some(
        (andBlock) => Boolean(andBlock?.entityType) && Boolean(andBlock?.entityId)
      );
    });
  }, []);

  // Mapping current nodes and edges to DTO for saving
  const mapNodesToDto = useCallback(() => {
    return mapCanvasContent({ nodes });
  }, [nodes]);

  const updateNodeData = useCallback(
    (nodeId: string, data: Record<string, unknown>) => {
      setNodes((prevNodes) => {
        const nextNodes = prevNodes.map((node) =>
          node.id === nodeId ? { ...node, data: { ...node.data, ...data } } : node
        );

        const updatedNode = nextNodes.find((node) => node.id === nodeId);
        const hasAddNode = nextNodes.some((node) => node.type === 'add');

        if (
          updatedNode &&
          updatedNode.type === 'enroll' &&
          updatedNode.id === INITIAL_ENROLL_NODE_ID &&
          !hasAddNode &&
          isFirstEnrollConfigured(updatedNode)
        ) {
          const addNodeId = `add-${crypto.randomUUID()}`;
          const addNode: Node = {
            id: addNodeId,
            type: 'add',
            position: {
              x: updatedNode.position.x + LAYOUT.enrollWidth + LAYOUT.gapX,
              y: updatedNode.position.y,
            },
            data: {},
          };

          setEdges((prevEdges) => [
            ...prevEdges,
            {
              id: `e-${crypto.randomUUID()}`,
              source: updatedNode.id,
              target: addNodeId,
              type: 'smoothstep',
            },
          ]);

          return [...nextNodes, addNode];
        }

        return nextNodes;
      });
    },
    [isFirstEnrollConfigured]
  );

  const isGraphValidForCreate = useCallback(() => {
    const firstEnrollNode = nodes.find(
      (n) => n.id === INITIAL_ENROLL_NODE_ID && n.type === NodeType.Enroll
    );
    if (!firstEnrollNode) {
      return false;
    }

    return isFirstEnrollConfigured(firstEnrollNode);
  }, [nodes, isFirstEnrollConfigured]);

  return {
    nodes: nodesWithHandlers,
    edges,
    isNodeSidebarOpen,
    addEnroll,
    openSidebar,
    closeSidebar,
    addNodeAtPlaceholder,
    isGraphValidForCreate,
    onNodesChange,
    onEdgesChange,
    mapNodesToDto,
    updateNodeData,
  };
};
